import React from 'react';
import { FmTabsList, FmTabsTrigger } from './FmTabs';
import { FmAssetImage } from './FmAssetImage';
import { ReportNavBackAction } from './ReportNavBackAction';
import { FmHeading, FmText } from './FmComposition';
import { FmSplit } from './FmLayout';
import { FmSelect } from './FmSelect';
import type { FmDeviceTier } from './FmResponsive';
import type {
  FmOperationalStageDescriptor,
  FmOperationalWorkspaceDescriptor,
} from './FmOperationalWorkspaceRoot.types';
import { operationalWorkspaceStatus } from './FmOperationalWorkspaceStatus';
import { resolveGovernedMediaIdentity } from '@/shared/config/contextualForestMedia';

export function OperationalWorkspaceHeader({
  descriptor,
  tier,
}: {
  descriptor: FmOperationalWorkspaceDescriptor;
  tier: FmDeviceTier;
}): React.ReactElement {
  /* Derived, never a served literal: a pending mutation or a conflicted
     revision owns the rail, so it can never read "ready" while the workspace is
     saving, deleting, or conflicted. */
  const status = operationalWorkspaceStatus(descriptor);
  const mastheadKey = descriptor.masthead
    ? resolveGovernedMediaIdentity({
        mediaContext: descriptor.masthead.mediaContext,
      })
    : null;
  return (
    <header
      className="fm-operational-workspace__header"
      data-masthead={mastheadKey ? '' : undefined}
    >
      {mastheadKey ? (
        <>
          <div className="fm-operational-workspace__masthead-media">
            <FmAssetImage assetKey={mastheadKey} cropRole="background" />
          </div>
          <div className="fm-operational-workspace__masthead-scrim" aria-hidden="true" />
        </>
      ) : null}
      <div className="fm-operational-workspace__header-inner">
        {/* The way back is its own line with a back arrow, so it reads as a
            control and never runs into the eyebrow. */}
        {descriptor.returnAction ? (
          <div className="fm-operational-workspace__return">
            <ReportNavBackAction
              action={descriptor.returnAction}
              testId="operational-return-action"
            />
          </div>
        ) : null}
        <span className="fm-operational-workspace__eyebrow">{descriptor.vocabulary.workspace}</span>
        {/* The title takes the wide track; the revision and status read in the
            split's narrow panel beside it. */}
        <FmSplit
          balance="copy-narrow"
          side="start"
          align="center"
          stackOrder="graphic-first"
          contentSurface="plain"
          className="fm-operational-workspace__title-row"
          graphic={
            <div>
              <FmHeading level={1} visualLevel={tier === 'mobile' ? 4 : 1}>
                {descriptor.title}
              </FmHeading>
              {/* On a phone the masthead stays short so the open step's answer
                  reaches the first screen; the status beside the title still
                  names the workspace state, and the step restates the scope. */}
              {descriptor.description && tier !== 'mobile' ? (
                <FmText>{descriptor.description}</FmText>
              ) : null}
            </div>
          }
          content={
            <dl className="fm-operational-workspace__meta">
              {/* Derived from the SAME state the status row is derived from. The
                two sat two lines apart reading "DATA — Plan revision 12" over
                "STATUS — No plan yet": the state whose entire point is that no
                plan exists printed a plan revision number as its data identity. */}
              <div
                data-status={
                  descriptor.state === 'empty' ? 'unavailable' : descriptor.revision.status
                }
                data-testid="operational-workspace-revision"
              >
                <dt>{descriptor.vocabulary.dataRevision}</dt>
                <dd>
                  {descriptor.state === 'empty' ? descriptor.emptyLabel : descriptor.revision.label}
                </dd>
              </div>
              <div data-status={status.key} data-testid="operational-workspace-status">
                <dt>{descriptor.vocabulary.status}</dt>
                <dd>{status.label}</dd>
              </div>
            </dl>
          }
        />
      </div>
    </header>
  );
}

export function OperationalWorkspaceFilters({
  descriptor,
}: {
  descriptor: FmOperationalWorkspaceDescriptor;
}): React.ReactElement | null {
  if (!descriptor.filters.length) return null;
  return (
    <search className="fm-operational-workspace__filters" aria-label={descriptor.title}>
      <div className="fm-operational-workspace__filters-inner">
        {descriptor.filters.map((filter) => (
          <div className="fm-operational-workspace__filter" key={filter.id}>
            <label htmlFor={`${descriptor.id}-${filter.id}`}>{filter.label}</label>
            <FmSelect<string>
              id={`${descriptor.id}-${filter.id}`}
              ariaLabel={filter.label}
              value={filter.value}
              options={filter.options.map((option) => ({ ...option }))}
              showSearch={filter.searchable}
              optionFilterProp="label"
              controlWidth="lg"
              onChange={(value) =>
                descriptor.onEvent?.({
                  kind: 'filter-change',
                  filterId: filter.id,
                  value,
                  expectedRevisionId: descriptor.revision.id,
                })
              }
              data-testid={`operational-filter-${filter.id}`}
            />
          </div>
        ))}
      </div>
    </search>
  );
}

/**
 * The stage strip fits its rail.
 *
 * There is no scroller: with six ratified stages a scroller overflows at 1440 as
 * well as at 390, and centring the ACTIVE stage only chooses which neighbour gets
 * clipped. The stages share the rail at every width, dropping the subtitle on a
 * phone and whenever the set is long. A label elides; it never clips mid-glyph.
 */
const DENSE_STAGE_COUNT = 5;

/**
 * `rail`: the plain stage strip, or the guided stepper under the standard
 * header. `docked`: the compact guided chrome's stepper inside its 56 px bar.
 * Each owns its own classes, so neither restyles the other's internals.
 */
export type OperationalStageNavigationVariant = 'rail' | 'docked';

const STAGE_CLASSES: Record<
  OperationalStageNavigationVariant,
  { list: string; stage: string; number: string; copy: string }
> = {
  rail: {
    list: 'fm-operational-workspace__stages',
    stage: 'fm-operational-workspace__stage',
    number: 'fm-operational-workspace__stage-number',
    copy: 'fm-operational-workspace__stage-copy',
  },
  docked: {
    list: 'fm-guided-chrome__steps',
    stage: 'fm-guided-chrome__step',
    number: 'fm-guided-chrome__step-number',
    copy: 'fm-guided-chrome__step-label',
  },
};

export function OperationalWorkspaceStageNavigation({
  descriptor,
  activeStageId,
  stagesTestId,
  onStageChange,
  variant = 'rail',
}: {
  descriptor: FmOperationalWorkspaceDescriptor;
  activeStageId: string;
  stagesTestId: string;
  onStageChange: (stage: FmOperationalStageDescriptor) => void;
  variant?: OperationalStageNavigationVariant;
}): React.ReactElement {
  const stageRailRef = React.useRef<HTMLDivElement>(null);
  React.useLayoutEffect(() => {
    stageRailRef.current
      ?.querySelector<HTMLElement>("[role='tab'][aria-selected='true']")
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeStageId]);
  const classes = STAGE_CLASSES[variant];
  const presentation =
    variant === 'docked' ? 'docked-stepper' : descriptor.guidance ? 'stepper' : undefined;

  /* Radix Tabs (rooted in OperationalWorkspaceFrame) owns the tab roles, roving
     focus and Arrow/Home/End movement. Each stage stays a real link to its panel
     anchor, and only the selected stage names a panel: no other panel is mounted. */
  return (
    <FmTabsList
      ref={stageRailRef}
      className={classes.list}
      aria-label={descriptor.title}
      data-dense={descriptor.stages.length >= DENSE_STAGE_COUNT ? 'true' : undefined}
      data-presentation={presentation}
      data-testid={stagesTestId}
    >
      {descriptor.stages.map((stage, stageIndex) => (
        <FmTabsTrigger key={stage.id} value={stage.id} asChild>
          <a
            id={`${descriptor.id}-${stage.id}-tab`}
            className={classes.stage}
            href={`#${descriptor.id}-${stage.id}`}
            type={undefined}
            aria-controls={stage.id === activeStageId ? `${descriptor.id}-${stage.id}` : undefined}
            data-status={
              stage.id === activeStageId
                ? 'current'
                : stage.status === 'current'
                  ? 'available'
                  : stage.status
            }
            aria-current={stage.id === activeStageId ? 'page' : undefined}
            aria-disabled={stage.status === 'blocked' || undefined}
            title={stage.disabledReason}
            onClick={(event) => event.preventDefault()}
            onKeyDown={(event) => {
              // Space on a link scrolls the page; here it activates the stage.
              if (event.key !== ' ') return;
              event.preventDefault();
              if (stage.status !== 'blocked') onStageChange(stage);
            }}
          >
            <span className={classes.number}>{String(stageIndex + 1).padStart(2, '0')}</span>
            <span className={classes.copy}>
              <strong>{stage.label}</strong>
              {stage.description && variant === 'rail' ? <small>{stage.description}</small> : null}
            </span>
          </a>
        </FmTabsTrigger>
      ))}
    </FmTabsList>
  );
}
